import { TrendingUp, Target, Shield, Brain, BarChart3, Users, Rocket, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import WaitlistForm from '@/components/WaitlistForm';

export default function About() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const onGoHome = () => navigate('/');
  const onShowLogin = () => navigate('/?login=1');
  const problems = [
    'Inconsistent risk management',
    'Emotional decision-making',
    'Lack of structured evaluation of signals',
    'Difficulty tracking performance objectively',
    'Overtrading during volatile conditions'
  ];

  const solutionComponents = [
    {
      title: 'AI-based signal scoring',
      description: 'Signals are evaluated using trend strength, pullback quality, probability metrics, and contextual market conditions.'
    },
    {
      title: 'Automated risk management',
      description: 'Position sizing, stop-loss placement, and take-profit levels are calculated based on predefined risk rules.'
    },
    {
      title: 'Paper trading automation',
      description: 'High-probability signals can be executed automatically in a simulated trading environment, allowing users to test strategies safely.'
    },
    {
      title: 'Performance analytics',
      description: 'Users can track win rate, expectancy, signal quality, and pattern performance over time.'
    },
    {
      title: 'Multi-asset compatibility',
      description: 'The platform supports indices, forex, and crypto signals generated via TradingView.'
    }
  ];

  const valueProps = [
    'Consistency',
    'Risk control',
    'Decision quality',
    'Performance transparency'
  ];

  const targetUsers = [
    'Active retail traders using TradingView strategies',
    'Systematic traders seeking structured execution workflows',
    'Traders aiming to improve discipline and consistency',
    'Users testing automation strategies in a controlled environment'
  ];

  const competitivePoints = [
    'Decision support rather than signal generation',
    'Risk-first trade structuring',
    'Integration with existing TradingView workflows',
    'Transparent performance tracking',
    'Automation-ready architecture'
  ];

  const futureDirections = [
    'Expanded scoring models',
    'Portfolio-level risk optimisation',
    'Advanced performance analytics',
    'Live broker integrations',
    'Adaptive strategy optimisation tools'
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0c]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={onGoHome} data-testid="about-logo-home">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg">Trading Pilot <span className="text-blue-500">360</span></span>
            </div>
            <nav className="flex items-center space-x-6">
              <button
                onClick={onGoHome}
                className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                data-testid="about-nav-home"
              >
                {user ? 'Dashboard' : 'Home'}
              </button>
              <span className="text-white text-sm font-medium">About</span>
              {!user && (
                <Button
                  onClick={onShowLogin}
                  variant="outline"
                  className="border-white/20 hover:bg-white/10"
                  data-testid="about-nav-login"
                >
                  Login
                </Button>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Section 1: Introduction */}
          <section className="mb-16">
            <h1 className="text-3xl sm:text-4xl font-bold mb-6">
              About <span className="text-blue-500">Trading Pilot 360</span>
            </h1>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                Trading Pilot 360 is an AI-assisted decision support platform designed to help traders 
                execute disciplined strategies through structured signal evaluation, automated risk 
                management, and performance analytics.
              </p>
              <p>
                The platform integrates directly with TradingView alerts, enabling users to transform 
                technical trading signals into structured, risk-controlled trade decisions. By combining 
                intelligent scoring models with predefined risk parameters, Trading Pilot 360 reduces 
                emotional decision-making and improves consistency in trade execution.
              </p>
              <p>
                Trading Pilot 360 operates as a modular decision-support layer between signal generation 
                and trade execution, providing traders with greater transparency, discipline, and 
                analytical insight.
              </p>
            </div>
          </section>

          {/* Section 2: Problem */}
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">Problem</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6">
              Many retail traders rely on technical indicators and signals but struggle with:
            </p>
            <ul className="space-y-3 mb-6">
              {problems.map((problem, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <ChevronRight className="w-5 h-5 text-red-500/70 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{problem}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-400 leading-relaxed">
              Even when traders use TradingView strategies, the process from signal to execution often 
              remains manual and inconsistent. This leads to unpredictable results and increased 
              exposure to unnecessary risk.
            </p>
          </section>

          {/* Section 3: Solution */}
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-green-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">Solution</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-8">
              Trading Pilot 360 introduces an intelligent evaluation layer that processes incoming 
              signals and applies structured decision logic before trade execution.
            </p>
            <h3 className="text-lg font-semibold text-white mb-4">Key components include:</h3>
            <div className="space-y-6">
              {solutionComponents.map((component, index) => (
                <div key={index} className="bg-[#13131a] border border-white/5 rounded-lg p-5">
                  <h4 className="font-semibold text-blue-400 mb-2">{component.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{component.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4: Value Proposition */}
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">Value Proposition</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6">
              Trading Pilot 360 provides traders with a structured framework that bridges the gap 
              between signal generation and disciplined execution.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">The platform focuses on:</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {valueProps.map((prop, index) => (
                <div key={index} className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 text-center">
                  <span className="text-blue-400 font-medium text-sm">{prop}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-400 leading-relaxed">
              Rather than replacing trading strategies, Trading Pilot 360 enhances them through 
              structured evaluation and automation readiness.
            </p>
          </section>

          {/* Section 5: Target Users */}
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">Target Users</h2>
            </div>
            <ul className="space-y-3">
              {targetUsers.map((user, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <ChevronRight className="w-5 h-5 text-purple-500/70 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{user}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 6: Competitive Positioning */}
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-orange-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">Competitive Positioning</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6">
              Trading Pilot 360 differentiates itself from traditional signal providers by focusing on:
            </p>
            <ul className="space-y-3 mb-6">
              {competitivePoints.map((point, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <ChevronRight className="w-5 h-5 text-orange-500/70 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{point}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-400 leading-relaxed">
              The platform acts as a control layer that helps traders manage risk and evaluate 
              signals more objectively.
            </p>
          </section>

          {/* Section 7: Long-Term Vision */}
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                <Rocket className="w-5 h-5 text-cyan-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">Long-Term Vision</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6">
              Trading Pilot 360 aims to become a comprehensive decision-support infrastructure for 
              retail and semi-systematic traders, enabling more disciplined execution of trading 
              strategies across multiple asset classes.
            </p>
            <h3 className="text-lg font-semibold text-white mb-4">Future development directions include:</h3>
            <ul className="space-y-3">
              {futureDirections.map((direction, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <ChevronRight className="w-5 h-5 text-cyan-500/70 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{direction}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 8: Summary Box */}
          <section className="mb-16">
            <Card className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/20 p-8">
              <p className="text-gray-200 leading-relaxed text-center text-lg">
                <span className="font-semibold text-white">Trading Pilot 360</span> is an AI-assisted 
                trading decision platform that helps traders convert TradingView signals into 
                structured, risk-controlled trade actions. By combining intelligent signal scoring, 
                automated risk management, and performance analytics, the platform enables more 
                disciplined and consistent execution of trading strategies while reducing emotional bias.
              </p>
            </Card>
          </section>

          {/* CTA */}
          {user ? (
            <section className="text-center">
              <Button
                onClick={onGoHome}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold"
                data-testid="about-cta-btn"
              >
                Go to Dashboard
              </Button>
            </section>
          ) : (
            <section id="about-waitlist" className="max-w-xl mx-auto">
              <WaitlistForm
                source="about_page"
                title="Join Early Access"
                subtitle="Be among the first traders to access Trading Pilot 360."
                subtext="No payment required. Early users will receive priority access to new features."
                buttonLabel="Join Early Access (Free)"
              />
            </section>
          )}

        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold">Trading Pilot <span className="text-blue-500">360</span></span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              <a href="/about" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-about-link">About</a>
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</a>
              <a href="/disclaimer" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-disclaimer-link">Disclaimer</a>
              <a href="/refund-policy" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-refund-link">Refund Policy</a>
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Trading Pilot 360. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
